import React from "react";
import type { IFieldOption } from "../Interface/fieldRendererInterface";
import type { TripTag, GroupOrderDetails, ExpenseCodeCacheData } from "../../../interfaces/ExpenseCodeTypes";
import { EMAIL_SUGGESTION_API_CALL, FIELD_TYPES } from "@payment-ui/helpers/FieldRenderer";
import { FIELD_IDS } from "../constants";

type FieldOption = IFieldOption;

const getRelevanceScore = (str: string, searchValue: string): number => {
    const lower = str.toLowerCase();
    const search = searchValue.toLowerCase();

    if (lower === search) return 3;
    if (lower.startsWith(search)) return 2;
    if (lower.includes(search)) return 1;
    return 0;
};

export const getFieldDisplayValue = (option: FieldOption): string => {
    return typeof option === "string" ? option : option.businessEmailId;
};

export const getFieldDisplayName = (option: FieldOption): string => {
    return typeof option === "string" ? option : `${option.firstName} ${option.lastName}`;
};

export const getFieldUniqueId = (option: FieldOption): string => {
    return typeof option === "string" ? option : option.businessEmailCommId;
};

export const getSortedOptions = (currentFieldValue: string, currentOptions: FieldOption[]) => {
    const currentValue = currentFieldValue?.toLowerCase();

    const optionsWithScores = currentOptions.map((option) => {
        const displayValue = getFieldDisplayValue(option);
        const uniqueId = getFieldUniqueId(option);
        const displayName =
            typeof option === "object" && "firstName" in option && "lastName" in option
                ? getFieldDisplayName(option)
                : undefined;
        return {
            displayValue,
            uniqueId,
            score: getRelevanceScore(displayValue, currentValue),
            secondaryText: displayName,
        };
    });

    const sortedWithScores = optionsWithScores.sort((a, b) => {
        return b.score - a.score || a.displayValue.toLowerCase().localeCompare(b.displayValue.toLowerCase());
    });

    const filtered = sortedWithScores.filter((item) => item.score > 0);

    return filtered.map((item, idx) => ({
        text: item.displayValue,
        id: idx + 1,
        data: {
            suggestion: item.displayValue,
            value: item.displayValue,
            uniqueId: item.uniqueId,
        },
        secondaryText: item.secondaryText,
    }));
};

export const filterStaticOptions = (searchValue: string, allOptions: FieldOption[]): FieldOption[] => {
    if (!searchValue || allOptions.length === 0) {
        return [];
    }

    return allOptions.filter((opt) => {
        const optValue = getFieldDisplayValue(opt);
        return optValue.toLowerCase().includes(searchValue.toLowerCase());
    });
};

export const extractSuggestion = (data?: Record<string, unknown>): string | null => {
    const suggestion = (data?.suggestion as string) || (data?.value as string);
    return suggestion || null;
};

export const isChipDuplicate = (chips: string[], newChip: string): boolean => {
    return chips.includes(newChip);
};

// Re-export shared types for convenience
export type { TripTag, GroupOrderDetails };

interface SubmitFieldConfig {
    id: string;
    label: string;
    type: string;
    required: boolean;
    apiCall?: string;
}

const isProjectCodeField = (id: string): boolean => id === FIELD_IDS.CODES_TO_SWIGGY;

const isPurposeOfOrderingField = (id: string): boolean => id === FIELD_IDS.PURPOSE_OF_ORDERING;

const isExternalGuestCountField = (id: string): boolean => id === FIELD_IDS.EXTERNAL_GUESTS;

export const getSubmitData = (
    fieldValues: Record<string, string>,
    chipsValues: Record<string, string[]>,
    chipUniqueIds: Record<string, string[]>,
    fields?: SubmitFieldConfig[],
) => {
    const tripTags: TripTag[] = [];
    let projectCode = "";
    let purposeOfOrdering = "";
    let externalGuestCount = "";
    let participantUniqueIds: string[] = [];
    let attendeeEmail: string[] = [];
    let attendeeEmailDisplay: string[] = [];

    fields?.forEach((field) => {
        if (!field) return;

        const fieldId = field.id;
        const value = fieldValues[fieldId] || "";

        if (isProjectCodeField(fieldId)) {
            projectCode = value;
            if (value) {
                tripTags.push({
                    attributeName: field.label,
                    attributeType: field.type,
                    attributeSelectedValue: [value],
                    mandatoryCheck: field.required,
                });
            }
            return;
        }

        if (isPurposeOfOrderingField(fieldId)) {
            purposeOfOrdering = value;
            return;
        }

        if (isExternalGuestCountField(fieldId)) {
            externalGuestCount = value;
            return;
        }

        if (field.type === FIELD_TYPES.MULTI_SUGGESTIVE_TYPING && field.apiCall === EMAIL_SUGGESTION_API_CALL) {
            const emailIds = chipUniqueIds[fieldId] || [];
            participantUniqueIds = emailIds;
            attendeeEmail = emailIds;
            attendeeEmailDisplay = chipsValues[fieldId] || [];
            return;
        }

        if (value) {
            tripTags.push({
                attributeName: field.label,
                attributeType: field.type,
                attributeSelectedValue: [value],
                mandatoryCheck: field.required,
            });
        }
    });

    const groupOrderDetails: GroupOrderDetails | undefined =
        participantUniqueIds.length > 0 || externalGuestCount
            ? {
                  participantDetails: participantUniqueIds.map((uid) => ({
                      participantEmailRef: uid,
                  })),
                  externalGuestCount,
              }
            : undefined;

    return {
        projectCode,
        purposeOfOrdering,
        tripTags,
        ...(groupOrderDetails && { groupOrderDetails }),
        ...(attendeeEmail.length > 0 && { attendeeEmail }),
        ...(attendeeEmailDisplay.length > 0 && { attendeeEmailDisplay }),
    };
};

export const getInitialValues = (
    fields: SubmitFieldConfig[] | undefined,
    cachedData: ExpenseCodeCacheData | null | undefined,
) => {
    const fieldValues: Record<string, string> = {};
    const chipsValues: Record<string, string[]> = {};
    const chipUniqueIds: Record<string, string[]> = {};

    if (!cachedData || !fields || fields.length === 0) {
        return { fieldValues, chipsValues, chipUniqueIds };
    }

    fields.forEach((field) => {
        if (!field) return;

        const fieldId = field.id;

        if (isProjectCodeField(fieldId)) {
            fieldValues[fieldId] = cachedData.projectCode || "";
            return;
        }

        if (isPurposeOfOrderingField(fieldId)) {
            fieldValues[fieldId] = cachedData.purposeOfOrdering || "";
            return;
        }

        if (isExternalGuestCountField(fieldId)) {
            fieldValues[fieldId] = cachedData.groupOrderDetails?.externalGuestCount || "";
            return;
        }

        if (field.type === FIELD_TYPES.MULTI_SUGGESTIVE_TYPING && field.apiCall === EMAIL_SUGGESTION_API_CALL) {
            chipsValues[fieldId] = cachedData.attendeeEmailDisplay || [];
            chipUniqueIds[fieldId] = cachedData.attendeeEmail || [];
            return;
        }

        const tripTag = cachedData.tripTags?.find((tag) => tag.attributeName === field.label);
        if (tripTag?.attributeSelectedValue?.length) {
            fieldValues[fieldId] = tripTag.attributeSelectedValue[0];
        }
    });

    return { fieldValues, chipsValues, chipUniqueIds };
};

export const getOrCreateInputRef = (
    fieldId: string,
    inputRefs: Record<string, React.RefObject<HTMLInputElement>>,
): React.RefObject<HTMLInputElement> => {
    if (!inputRefs[fieldId]) {
        inputRefs[fieldId] = React.createRef<HTMLInputElement>();
    }
    return inputRefs[fieldId];
};
